/**
 * Дашборд кабинета TouchBrand «stars» — единый пульт.
 *
 * Один вертикальный скролл-поток, mobile-first. Порядок сверху вниз = убывающая
 * срочность для человека, который НЕ знает продукт:
 *   (0) Светофор дня
 *   (1) Что горит сегодня (real, ЗАМОРОЖЕННЫЙ контракт /api/dashboard)
 *   (2) Деньги: путь рубля (real)
 *   (3) Воронка продаж + почему теряем (real)
 *   (4) Клиенты и откуда лиды (real)
 *   — разделитель «Скоро (демо)» —
 *   (5) Первая линия — Тоня (демо)
 *   (6) Подписка и тренды (демо)
 *
 * Жёсткое разделение real/demo: real — bg-card + чёткие цифры + цветные акценты;
 * demo — приглушённый фон + бейдж «демо» + подпись «появится после …».
 * Графики — чистый SVG/CSS, без новых npm. Данные: GET /api/dashboard (useDashboard).
 */

import { Flame } from 'lucide-react'
import { useDashboard } from '@/hooks/useDashboard'
import { useAuth } from '@/hooks/useAuth'
import { StatusBanner } from '@/components/dashboard/StatusBanner'
import { HeaderKpis } from '@/components/dashboard/HeaderKpis'
import { WhatsHot } from '@/components/dashboard/WhatsHot'
import { MoneyBand } from '@/components/dashboard/MoneyBand'
import { SalesFunnel } from '@/components/dashboard/SalesFunnel'
import { ClientsBlock } from '@/components/dashboard/ClientsBlock'
import { TonyaDemo } from '@/components/dashboard/TonyaDemo'
import { TrendsDemo } from '@/components/dashboard/TrendsDemo'
import { SectionTitle, DemoSectionDivider } from '@/components/dashboard/primitives'

export function DashboardPage() {
  const { data: dashboard, isLoading, error } = useDashboard()
  const { user } = useAuth()

  if (isLoading) {
    return <div className="p-4 text-muted-foreground">Загрузка дашборда…</div>
  }

  if (error || !dashboard) {
    return (
      <div className="space-y-4">
        <h1 className="text-xl font-semibold">Дашборд</h1>
        <p className="text-sm text-destructive">Ошибка загрузки данных</p>
      </div>
    )
  }

  return (
    <div className="space-y-8 pb-10">
      {/* 0 — Светофор дня */}
      <StatusBanner dashboard={dashboard} />

      {/* Шапка: приветствие, дата, главные KPI */}
      <HeaderKpis dashboard={dashboard} name={user?.display_name ?? ''} />

      {/* 1 — Что горит сегодня (real) */}
      <section className="space-y-3">
        <SectionTitle icon={Flame} title="Что горит сегодня" hint="очередь действий — сверху вниз" />
        <WhatsHot dashboard={dashboard} />
      </section>

      {/* 2 — Деньги: путь рубля (real) */}
      {dashboard.money && (
        <section className="space-y-3">
          <SectionTitle title="Деньги: путь рубля" hint="потенциал → договор → счёт" />
          <MoneyBand money={dashboard.money} />
        </section>
      )}

      {/* 3 — Воронка продаж + почему теряем (real) */}
      {dashboard.funnel && (
        <section className="space-y-3">
          <SectionTitle title="Воронка и конверсия" />
          <SalesFunnel funnel={dashboard.funnel} />
        </section>
      )}

      {/* 4 — Клиенты и откуда лиды (real) */}
      {dashboard.clients && (
        <section className="space-y-3">
          <SectionTitle title="Клиенты и откуда лиды" />
          <ClientsBlock clients={dashboard.clients} />
        </section>
      )}

      {/* ───── Demo-зона ───── */}
      {(dashboard.tonya || dashboard.trends) && (
        <DemoSectionDivider label="так пульт будет выглядеть с полными данными" />
      )}

      {/* 5 — Первая линия Тоня (демо) */}
      {dashboard.tonya && (
        <section className="space-y-3">
          <SectionTitle
            title="Первая линия — Тоня"
            hint={dashboard.tonya.demo_label}
          />
          <TonyaDemo tonya={dashboard.tonya} />
        </section>
      )}

      {/* 6 — Подписка и тренды (демо) */}
      {dashboard.trends && (
        <section className="space-y-3">
          <SectionTitle title="Подписка и тренды" hint={dashboard.trends.demo_label} />
          <TrendsDemo trends={dashboard.trends} />
        </section>
      )}
    </div>
  )
}
